/**
 * Copyright (C) 2026 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { Observable, of, timer } from 'rxjs';
import { switchMap, map, catchError, takeUntil } from 'rxjs/operators';
import { ofType, StateObservable } from 'redux-observable';
import { AjaxError, AjaxResponse } from 'rxjs/ajax';
import { Action, ActionCreators } from './actions';
import { RootState } from '../index';
import { handleErrorInterceptor } from '../../utils/AuthUtils';
import {
  getPropagatedStateStepMs,
  getSpaceVehiclePositionsAtTime,
  normalizeHaloNetSpaceVehicleOrbits,
  normalizeHaloNetSpaceVehicles,
} from '../../utils/HaloNetUtils';
import { OrbitPointsBySvNameDto, SpaceVehicleIdDto } from '../../shared/entities/haloNet';
import { SEC_TO_MS_CONVERSION } from '../../utils/DateTimeUtils';

// Used only if the config has not loaded, or carries a value that makes no sense.
const FALLBACK_POLLING_INTERVAL_SECONDS = 1800;

// Read haloNetPollingIntervalSeconds at runtime from the current state rather than when the epic is built.
// Prevents using a stale polling interval if the epic was built before the config was loaded.
const getHaloNetPollingIntervalSeconds = (state: RootState): number =>
  state.config?.appConfig?.haloNetPollingIntervalSeconds || FALLBACK_POLLING_INTERVAL_SECONDS;

const pollOnRequest = (action$: Observable<Action>, state$: StateObservable<RootState>) =>
  switchMap(
    ($action: Action): Observable<Action> =>
      timer(0, getHaloNetPollingIntervalSeconds(state$.value) * SEC_TO_MS_CONVERSION).pipe(
        map((): Action => $action),
        takeUntil(action$.pipe(ofType(ActionCreators.CancelHaloNetSpaceVehiclePolls.type))),
      ),
  );

// Used only when the orbit points do not reveal their own spacing.
const FALLBACK_POSITION_TICK_INTERVAL_MS = 15000;

const requestHaloNetSpaceVehiclesSuccess = (response: AjaxResponse<any>): Action => {
  const normalizedSpaceVehicles = normalizeHaloNetSpaceVehicles((response.response || []) as SpaceVehicleIdDto[]);
  return ActionCreators.ReceivedHaloNetSpaceVehiclesSuccess.create({ response: normalizedSpaceVehicles });
};

const requestHaloNetSpaceVehiclesError = (error: AjaxError): Action => {
  return ActionCreators.ReceivedHaloNetSpaceVehiclesError.create(error);
};

export const requestHaloNetSpaceVehiclesEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestHaloNetSpaceVehicles.type),
    pollOnRequest(action$, state$),
    switchMap(
      (): Observable<Action> =>
        ajaxRaw.get('/halonet/space-vehicles').pipe(
          map((response: AjaxResponse<any>): Action => requestHaloNetSpaceVehiclesSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(error, of(requestHaloNetSpaceVehiclesError(error))),
          ),
        ),
    ),
  );

const requestHaloNetSpaceVehicleOrbitsSuccess = (response: AjaxResponse<any>): Action => {
  const normalizedOrbits = normalizeHaloNetSpaceVehicleOrbits((response.response || {}) as OrbitPointsBySvNameDto);
  return ActionCreators.ReceivedHaloNetSpaceVehicleOrbitsSuccess.create({ response: normalizedOrbits });
};

const requestHaloNetSpaceVehicleOrbitsError = (error: AjaxError): Action => {
  return ActionCreators.ReceivedHaloNetSpaceVehicleOrbitsError.create(error);
};

export const requestHaloNetSpaceVehicleOrbitsEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestHaloNetSpaceVehicleOrbits.type),
    pollOnRequest(action$, state$),
    switchMap(
      (): Observable<Action> =>
        ajaxRaw.get('/halonet/space-vehicles/orbits').pipe(
          map((response: AjaxResponse<any>): Action => requestHaloNetSpaceVehicleOrbitsSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(error, of(requestHaloNetSpaceVehicleOrbitsError(error))),
          ),
        ),
    ),
  );

/**
 * Move each space vehicle to the propagated point nearest the present time.
 *
 * This is run at the same interval as the propagated state time step.
 */
export const haloNetSpaceVehiclePositionTickEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.ReceivedHaloNetSpaceVehicleOrbitsSuccess.type),
    switchMap((): Observable<Action> => {
      const orbitsBySpaceVehicleId = state$.value.haloNetSpaceVehicles.orbitsBySpaceVehicleId;
      const tickIntervalMs = getPropagatedStateStepMs(orbitsBySpaceVehicleId) ?? FALLBACK_POSITION_TICK_INTERVAL_MS;

      return timer(0, tickIntervalMs).pipe(
        map((): Action => {
          const positionsBySvName = getSpaceVehiclePositionsAtTime(
            state$.value.haloNetSpaceVehicles.orbitsBySpaceVehicleId,
            new Date(),
          );
          return ActionCreators.UpdateHaloNetSpaceVehiclePositions.create({ positionsBySvName });
        }),
        takeUntil(action$.pipe(ofType(ActionCreators.CancelHaloNetSpaceVehiclePolls.type))),
      );
    }),
  );
